import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty, toNumber, toString } from 'lodash';
import queryString, { ParsedQuery } from 'query-string';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FirmsService } from 'api';
import { setAllocationInfo } from 'common/actions/allocations';
import { EDIT_FUND_INVESTMENT, EXCEL_EXPORT } from 'common/constants/pageActions';
import { DATE_QUERY_PARAM } from 'common/constants/pageFilters';
import { useStore } from 'common/store';
import { ExtraAction, LayoutContextValues, NavItems, UseStoreValues } from 'common/types/store';
import { Alert, AllocationFinalMessage, MessageBox, MissingMeasurementDateMessage } from 'components';
import { MIN_DRAWER_HEIGHT } from 'components/Drawer/tab-drawer/TabDrawer.constants';
import { LayoutContext, useFundInvestmentContext } from 'context';
import { useRemoveQueryParam } from 'hooks';
import { AllocationDialog as AllocationVersionDialog } from 'layouts/Main/components/AllocationDialog';
import { NewVersionForm as NewAllocationVersionDialog } from 'pages/Allocation/allocations/components';
import { FundInvestmentsParams } from 'pages/FundInvestments';
import {
  FUND_INVESTMENTS_DOCUMENTS_SLUG,
  FUND_INVESTMENTS_INVALID_CELLS_TITLE,
  FUND_INVESTMENTS_NO_FUND_SELECTED_TITLE,
  FUND_INVESTMENTS_NO_MEASUREMENT_DATE_MESSAGE,
  FUND_INVESTMENTS_NO_MEASUREMENT_DATE_TITLE,
} from 'pages/FundInvestments/common/constants';
import { FUND_INVESTMENTS_MENU_ITEMS } from 'pages/FundInvestments/common/pages';
import { FundInvestmentView } from 'pages/FundInvestments/components/FundInvestmentView';
import { useFundInvestmentRouteValidation } from 'pages/FundInvestments/hooks';
import { withFundInvestmentsProvider } from 'pages/FundInvestments/utils';
import {
  ALLOCATION_FORBIDDEN_TAGLINE,
  ALLOCATION_FORBIDDEN_TITLE,
} from 'pages/ValuationsAllocation/common/constants/allocation';
import {
  DEFAULT_VALUES,
  NOT_READY_FOR_AUDIT_MESSAGE,
  SCALAR_OWNED_DRAFT_MESSAGE,
  VALUATIONS_FINAL_VALUATION_LOCKED_MESSAGE,
  VALUATIONS_SPREADSHEET_TABLE_TERMS,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { InvalidTablesMessage } from 'pages/ValuationsAllocation/components';
import { CreateNewVersionButton } from 'pages/ValuationsAllocation/components/CreateNewVersionButton';
import { useDialogFormsStore } from 'store';
import { extractDate, getObjectValue, getStringValue, Logger } from 'utilities';
import { GetFundInvestmentPageUrl } from './FundInvestmentContainer.types';

const FundInvestmentContainer: FC = () => {
  // Validate the route for the current fund investment
  useFundInvestmentRouteValidation();

  const [storeValue] = useStore() as UseStoreValues;
  const { companyInfo: currentFundInvestment, firmInfo: currentFirm } = getObjectValue(storeValue);
  const {
    id: currentFundInvestmentId,
    measurement_date_id: currentMeasurementDateId,
    name: currentFundInvestmentName,
    slug: currentFundInvestmentSlug,
  } = getObjectValue(currentFundInvestment);
  const { id: currentFirmId, slug: currentFirmSlug } = getObjectValue(currentFirm);

  const navigate = useNavigate();
  const location = useLocation();
  const removeDateParam = useRemoveQueryParam();

  const { companySlugParam: fundInvestmentSlugParam = '', tableSlugParam: fundInvestmentTableSlugParam = '' } =
    useParams<FundInvestmentsParams>();

  const {
    setAlertMessage,
    setCommonMeasurementDate,
    setNavItems,
    setPageTitle,
    setShowAlert,
    toggleExportDialog,
    updateExtraPageActions,
    viewOnlyUser: currentUserIsViewOnly,
  } = useContext(LayoutContext) as LayoutContextValues;

  // Fund investments context
  const {
    fundInvestmentAllocationStatus,
    fundInvestmentAllocationVersion,
    fundInvestmentSpreadsheet,
    fundInvestmentValuationAllocation,
    fundInvestmentValuationOwnership,
    fundInvestmentValuationReadyForAudit,
  } = useFundInvestmentContext();
  const {
    isCurrentAllocationDraft,
    isCurrentAllocationFinal,
    isCurrentAllocationPublished,
    isCurrentValuationDisabled,
    setIsCurrentValuationDisabled,
  } = fundInvestmentAllocationStatus;
  const {
    allocationVersions,
    closeNewAllocationVersionDialog,
    createNewAllocationVersion,
    createNewBlankAllocationVersion,
    getAllocationVersions,
    isAllocationVersionDialogOpen,
    isOpenNewAllocationVersionDialog,
    onCloseAllocationVersionDialog,
    setNewAllocationVersionFormState,
  } = fundInvestmentAllocationVersion;
  const { hasInvalidFundSelections, hasValidationErrors } = fundInvestmentSpreadsheet;
  const {
    currentAllocation,
    currentAllocationVersion,
    isCurrentAllocationAccessForbidden,
    isLoadingFundInvestmentByAllocation,
    isLoadingValuationByAllocation,
    MeasurementDateDialog,
  } = fundInvestmentValuationAllocation;
  const { fund_investment_ownership_id: currentAllocationFundInvestmentOwnershipId } =
    getObjectValue(currentAllocationVersion);
  const {
    isCurrentFirmUserReadOnly,
    isCurrentUserAuditor,
    setCurrentFundInvestmentOwnershipId,
    isCurrentValuationFirmOwned,
  } = fundInvestmentValuationOwnership;
  const { isCurrentValuationReadyForAudit } = fundInvestmentValuationReadyForAudit;

  // Dialog forms store
  const setDisplayFundInvestmentDialogForm = useDialogFormsStore(state => state.setDisplayFundInvestmentDialogForm);
  const setShouldUpdateFundInvestment = useDialogFormsStore(state => state.setShouldUpdateFundInvestment);

  // Tracks if fund investment details have been requested
  const [hasAttemptedToLoadDetails, setHasAttemptedToLoadDetails] = useState(false);

  const firmSlug = useMemo<string>(() => getStringValue(currentFirmSlug), [currentFirmSlug]);
  const fundInvestmentSlug = useMemo<string>(
    () => getStringValue(currentFundInvestmentSlug),
    [currentFundInvestmentSlug]
  );

  const isInitializing = useMemo<boolean>(
    () => !firmSlug || !fundInvestmentSlug || fundInvestmentSlugParam !== fundInvestmentSlug,

    [firmSlug, fundInvestmentSlug, fundInvestmentSlugParam]
  );

  const isPageReady = useMemo<boolean>(
    () =>
      (!isEmpty(currentAllocationVersion) ? hasAttemptedToLoadDetails : true) &&
      !isInitializing &&
      !isLoadingFundInvestmentByAllocation &&
      !isLoadingValuationByAllocation &&
      (!isEmpty(currentAllocationVersion) ? isCurrentValuationFirmOwned !== undefined : true),
    [
      hasAttemptedToLoadDetails,
      isInitializing,
      isLoadingFundInvestmentByAllocation,
      isLoadingValuationByAllocation,
      isCurrentValuationFirmOwned,
      currentAllocationVersion,
    ]
  );

  const parsedQuery = useMemo<ParsedQuery<string>>(() => queryString.parse(location.search), [location]);
  const dateQueryParam = useMemo<string>(() => toString(parsedQuery?.[DATE_QUERY_PARAM]), [parsedQuery]);

  const shouldShowAlerts = useMemo<boolean>(
    () => ![FUND_INVESTMENTS_DOCUMENTS_SLUG].includes(fundInvestmentTableSlugParam),
    [fundInvestmentTableSlugParam]
  );

  const navigateToFundInvestmentUrl = useCallback(
    (getFundInvestmentPageUrl: GetFundInvestmentPageUrl) => {
      if (!isInitializing) {
        const fundInvestmentPageUrl = getFundInvestmentPageUrl(firmSlug, fundInvestmentSlug);

        navigate({ pathname: fundInvestmentPageUrl, search: queryString.stringify(parsedQuery) });
      }
    },
    [firmSlug, fundInvestmentSlug, isInitializing, navigate, parsedQuery]
  );

  const fundInvestmentMenuItems = useMemo<NavItems[]>(
    () =>
      FUND_INVESTMENTS_MENU_ITEMS.map(
        item =>
          ({
            ...item,
            callback: () => navigateToFundInvestmentUrl(item.callback),
          }) as NavItems
      ),
    [navigateToFundInvestmentUrl]
  );

  const openUpdateFundInvestmentFormDialog = useCallback(() => {
    setShouldUpdateFundInvestment(true);
    setDisplayFundInvestmentDialogForm(true);
  }, [setDisplayFundInvestmentDialogForm, setShouldUpdateFundInvestment]);

  const fundInvestmentExtraActions = useMemo<ExtraAction[]>(
    () => [
      {
        label: EDIT_FUND_INVESTMENT,
        callback: openUpdateFundInvestmentFormDialog,
        isActive: true,
      } as ExtraAction,
      {
        label: EXCEL_EXPORT,
        callback: toggleExportDialog,
        isActive: Boolean(currentMeasurementDateId),
      } as ExtraAction,
    ],
    [currentMeasurementDateId, openUpdateFundInvestmentFormDialog, toggleExportDialog]
  );

  const getFundInvestmentDetails = useCallback(async () => {
    if (currentFirmId && currentFundInvestmentId && currentMeasurementDateId) {
      try {
        const fundInvestmentDetails = await FirmsService.apiFirmsFundInvestmentRead(
          toString(currentFirmId),
          toString(currentFundInvestmentId)
        );

        if (fundInvestmentDetails) {
          const { fund_investment_ownership_id: fundInvestmentOwnershipId } = getObjectValue(fundInvestmentDetails);

          if (fundInvestmentOwnershipId) {
            setCurrentFundInvestmentOwnershipId(
              toNumber(currentAllocationFundInvestmentOwnershipId) || toNumber(fundInvestmentOwnershipId) || null
            );
          }
        }
      } catch (error) {
        Logger.error('Error fetching fund investment details', error);
      } finally {
        setHasAttemptedToLoadDetails(true);
      }
    } else {
      // If we don't have the required data, mark as attempted
      setHasAttemptedToLoadDetails(true);
    }
  }, [
    currentAllocationFundInvestmentOwnershipId,
    currentFirmId,
    currentFundInvestmentId,
    currentMeasurementDateId,
    setCurrentFundInvestmentOwnershipId,
  ]);

  // Fetch fund investment details
  useEffect(() => {
    if (!isInitializing && currentFirmId && currentFundInvestmentId && !isEmpty(currentAllocationVersion)) {
      getFundInvestmentDetails();
    }
  }, [currentAllocationVersion, currentFirmId, currentFundInvestmentId, getFundInvestmentDetails, isInitializing]);

  // Sync measurement date from the query param and clean it up if invalid
  useEffect(() => {
    if (dateQueryParam) {
      setCommonMeasurementDate?.(
        getStringValue(
          extractDate({
            dateSlug: dateQueryParam,
          })
        )
      );

      if (!isEmpty(currentFundInvestment) && !currentMeasurementDateId) {
        // Remove the date query param
        removeDateParam(DATE_QUERY_PARAM);
      }
    }
  }, [currentFundInvestment, currentMeasurementDateId, dateQueryParam, removeDateParam, setCommonMeasurementDate]);

  // Set up navigation, page title, and extra actions
  useEffect(() => {
    if (!isEmpty(currentFundInvestment)) {
      setPageTitle?.(getStringValue(currentFundInvestmentName));
      setNavItems?.(fundInvestmentMenuItems);
      updateExtraPageActions?.(fundInvestmentExtraActions);
    }
    // Cleanup on dependencies change or unmount
    return () => {
      setPageTitle?.(null);
      setNavItems?.(null);
      updateExtraPageActions?.([]);
    };
  }, [
    currentFundInvestment,
    currentFundInvestmentName,
    fundInvestmentMenuItems,
    fundInvestmentExtraActions,
    setNavItems,
    setPageTitle,
    updateExtraPageActions,
  ]);

  // Reset alert message
  useEffect(
    () => () => {
      setShowAlert?.(false);
      setAlertMessage?.('');
    },
    [location, setAlertMessage, setShowAlert]
  );

  // Wait until everything is ready
  if (!isPageReady) {
    return null;
  }

  // User has not access to the allocation
  if (isCurrentAllocationAccessForbidden) {
    return <MessageBox fullWidth={false} tagline={ALLOCATION_FORBIDDEN_TAGLINE} title={ALLOCATION_FORBIDDEN_TITLE} />;
  }

  // Firm users cannot access draft Scalar valuation
  if (isCurrentFirmUserReadOnly && isCurrentAllocationDraft) {
    return (
      <MessageBox
        action={<CreateNewVersionButton onClickHandler={createNewBlankAllocationVersion} />}
        fullWidth={false}
        title={SCALAR_OWNED_DRAFT_MESSAGE}
        titleStyleOverride={{ whiteSpace: 'pre-line' }}
      />
    );
  }

  // Auditor can only see published/final and ready for audit
  if (isCurrentUserAuditor) {
    if (
      isCurrentAllocationDraft ||
      ((isCurrentAllocationPublished || isCurrentAllocationFinal) && !isCurrentValuationReadyForAudit)
    ) {
      return (
        <MessageBox
          fullWidth={false}
          title={NOT_READY_FOR_AUDIT_MESSAGE}
          titleStyleOverride={{ whiteSpace: 'pre-line' }}
        />
      );
    }
  }

  // Measurement date is not set
  if (!currentMeasurementDateId) {
    return (
      <MissingMeasurementDateMessage
        tagline={FUND_INVESTMENTS_NO_MEASUREMENT_DATE_MESSAGE}
        title={FUND_INVESTMENTS_NO_MEASUREMENT_DATE_TITLE}
      />
    );
  }

  return (
    <div style={{ marginBottom: MIN_DRAWER_HEIGHT }}>
      {/* Show alerts only on applicable pages */}
      {shouldShowAlerts && (
        <>
          {/* Unlock final allocation message */}
          {isCurrentAllocationFinal && isCurrentValuationDisabled && (
            <AllocationFinalMessage
              finalAllocations={[]}
              setIsDisabled={setIsCurrentValuationDisabled}
              singleAlert
              tableTerms={{ tableName: VALUATIONS_SPREADSHEET_TABLE_TERMS.tableName }}
              viewOnlyUser={currentUserIsViewOnly || isCurrentFirmUserReadOnly || isCurrentUserAuditor}
            />
          )}

          {/* Final allocation message */}
          {isCurrentAllocationFinal && !isCurrentValuationDisabled && (
            <Alert isAlertVisible>{VALUATIONS_FINAL_VALUATION_LOCKED_MESSAGE}</Alert>
          )}

          {/* Error message if there is any invalid cell */}
          {hasValidationErrors && (
            <InvalidTablesMessage
              title={
                hasInvalidFundSelections
                  ? FUND_INVESTMENTS_NO_FUND_SELECTED_TITLE
                  : FUND_INVESTMENTS_INVALID_CELLS_TITLE
              }
            />
          )}
        </>
      )}

      {/* Fund investment page */}
      <FundInvestmentView fundInvestmentTableSlugParam={fundInvestmentTableSlugParam} />

      {/* New allocation version dialog */}
      {isOpenNewAllocationVersionDialog && (
        <NewAllocationVersionDialog
          defaultValues={DEFAULT_VALUES}
          onClose={closeNewAllocationVersionDialog}
          onFormChange={setNewAllocationVersionFormState}
          onSave={createNewAllocationVersion}
          open={isOpenNewAllocationVersionDialog}
        />
      )}

      {/* Allocation versions dialog */}
      {isAllocationVersionDialogOpen && (
        <AllocationVersionDialog
          allocationInfo={currentAllocation}
          getVersions={getAllocationVersions}
          onClose={onCloseAllocationVersionDialog}
          open
          setAllocationInfo={setAllocationInfo}
          versions={allocationVersions}
        />
      )}

      {/* Measurement date dialog */}
      {MeasurementDateDialog && <MeasurementDateDialog />}
    </div>
  );
};

export default withFundInvestmentsProvider(FundInvestmentContainer);
